import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  ThemeProvider,
  createTheme,
  CssBaseline,
  alpha,
  styled,
  Grid,
  Fade,
  Zoom,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  MedicalServices,
  AdminPanelSettings,
  AccountCircle,
  Brightness4,
  Brightness7,
  Google,
  Security,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { useTranslation } from 'react-i18next';
import { login } from '../../services/auth';
import api from '../../services/api';
import ForgotPasswordDialog from './ForgotPassword';
import FullLogo from '../logo/FullLogo';

// Tesla-inspired color palette
const teslaColors = {
  dark: {
    primary: '#3E6AE1', // Tesla blue
    secondary: '#34C3FF', // Electric teal
    background: '#121212',
    surface: '#1E1E1E',
    card: 'rgba(30, 30, 30, 0.8)',
    text: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(255, 255, 255, 0.12)',
  },
  light: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#F5F5F7',
    surface: '#FFFFFF',
    card: 'rgba(255, 255, 255, 0.8)',
    text: '#121212',
    textSecondary: 'rgba(18, 18, 18, 0.7)',
    border: 'rgba(0, 0, 0, 0.12)',
  }
};

// Simplified card component
const AuthCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(30, 30, 30, 0.95)' 
    : 'rgba(255, 255, 255, 0.95)',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.12)' 
    : 'rgba(0, 0, 0, 0.12)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 6px rgba(0, 0, 0, 0.1)'
    : '0 4px 6px rgba(0, 0, 0, 0.05)',
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: '564px',
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  background: '#0067BC',
  border: 0,
  borderRadius: '6px',
  color: 'white',
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '16px',
  textTransform: 'none',
  '&:hover': {
    background: '#0055A3',
  },
  '&:disabled': {
    background: '#A9C7DF',
    color: 'white',
    cursor: 'not-allowed',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.02)',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0067BC',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0067BC',
      borderWidth: '1px',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '6px',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.02)',
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0067BC',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0067BC',
    borderWidth: '1px',
  },
}));

// Create light and dark themes
const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light mode palette
          primary: {
            main: teslaColors.light.primary,
          },
          secondary: {
            main: teslaColors.light.secondary,
          },
          background: {
            default: teslaColors.light.background,
            paper: teslaColors.light.surface,
          },
          text: {
            primary: teslaColors.light.text,
            secondary: teslaColors.light.textSecondary,
          },
          divider: teslaColors.light.border,
        }
      : {
          // Dark mode palette
          primary: {
            main: teslaColors.dark.primary,
          },
          secondary: {
            main: teslaColors.dark.secondary,
          },
          background: {
            default: teslaColors.dark.background,
            paper: teslaColors.dark.surface,
          },
          text: {
            primary: teslaColors.dark.text,
            secondary: teslaColors.dark.textSecondary,
          },
          divider: teslaColors.dark.border,
        }),
  },
  typography: {
    fontFamily: '"Satoshi", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '14px',
          fontWeight: 500,
        },
      },
    },
  },
});

// Color mode context
export const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

function Login() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
    role: 'clinician',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  // MFA verification dialog state
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [mfaError, setMfaError] = useState('');
  
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { mode, toggleTheme: sharedToggleTheme } = useThemeMode();

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: sharedToggleTheme,
    }),
    [sharedToggleTheme],
  );

  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  // Handle Google OAuth callback
  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const pending = fragment.get('mfa_token');
    const token = fragment.get('token');
    if (pending || token) window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (pending) {
      setMfaToken(pending); setMfaDialogOpen(true); return;
    }
    const errorParam = searchParams.get('error');
    
    if (token) {
      // Successfully authenticated with Google
      // Store token and redirect
      authLogin({ role: 'clinician' }, token); // Will be updated by backend with actual user data
      navigate('/portal');
    } else if (errorParam) {
      setError(decodeURIComponent(errorParam).replace(/_/g, ' '));
    }
  }, [searchParams, authLogin, navigate]);

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await login({
        ...credentials,
        email: credentials.email.trim().toLowerCase(),
        password: credentials.password.trim(),
      });

      // MFA challenge — show TOTP dialog
      if (response.requiresMFA) {
        setMfaToken(response.mfaToken);
        setMfaDialogOpen(true);
        setLoading(false);
        return;
      }

      // Check approval status
      if (response.user.approvalStatus === 'pending') {
        setError('Your account is pending approval. Please wait for administrator approval.');
        setLoading(false);
        return;
      } else if (response.user.approvalStatus === 'rejected') {
        setError('Your account registration was not approved. Please contact administrator.');
        setLoading(false);
        return;
      }

      authLogin(response.user, response.token);

      // Navigate directly to the role's home — avoids the brief flash of the
      // portal-choice page that happens when every role routes through /portal first.
      const role = response.user.role;
      if (role === 'radiologist') {
        const path = response.user.radiologistType !== 'facility' ? '/radiologist/reader-portal' : '/radiologist';
        navigate(path);
        return;
      }
      const directPaths = {
        admin: '/admin', tech_support: '/admin', product_lead: '/admin', customer_success: '/admin',
        technologist: '/technologist', radiographer: '/technologist',
        facility_admin: '/facility-admin',
        it_admin: '/it-admin',
        compliance_officer: '/compliance', compliance_internal: '/compliance',
        referring_physician: '/referring-physician', nurse: '/clinician',
        medical_records_officer: '/clinician', sonographer: '/clinician',
      };
      navigate(directPaths[role] || '/portal');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || t('auth.loginFailed'));
      setLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    setMfaVerifying(true);
    setMfaError('');
    try {
      const res = await api.post('/auth/mfa/verify', { mfaToken, code: mfaCode });
      if (res.data.success) {
        // Store token — same as the normal login flow
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
        setMfaDialogOpen(false);
        authLogin(res.data.user, res.data.token);
        const role = res.data.user.role;
        if (role === 'radiologist') {
          const path = res.data.user.radiologistType !== 'facility' ? '/radiologist/reader-portal' : '/radiologist';
          navigate(path);
          return;
        }
        const directPaths = {
          admin: '/admin', tech_support: '/admin', product_lead: '/admin', customer_success: '/admin',
          technologist: '/technologist', radiographer: '/technologist',
          facility_admin: '/facility-admin', it_admin: '/it-admin',
          compliance_officer: '/compliance', compliance_internal: '/compliance',
          referring_physician: '/referring-physician', nurse: '/clinician',
          medical_records_officer: '/clinician', sonographer: '/clinician',
        };
        navigate(directPaths[role] || '/portal');
      }
    } catch (err) {
      setMfaError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Google Sign-In handler
  const handleGoogleSignIn = () => {
    // Redirect to backend Google OAuth endpoint
    window.location.href = `${process.env.REACT_APP_API_URL || ''}/api/auth/google`;
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'clinician':
        return <MedicalServices sx={{ mr: 1.5, fontSize: '22px' }} />;
      case 'radiologist':
        return <Person sx={{ mr: 1.5, fontSize: '22px' }} />;
      case 'admin':
        return <AdminPanelSettings sx={{ mr: 1.5, fontSize: '22px' }} />;
      default:
        return <AccountCircle sx={{ mr: 1.5, fontSize: '22px' }} />;
    }
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            px: 2,
          }}
        >
          <AuthCard>
            {/* Logo */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <FullLogo />
            </Box>

            {/* Welcome Text */}
            <Box sx={{ mb: 4 }}>
              <Typography
                component="h1"
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#111827',
                }}
              >
                {t('auth.welcomeBack')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#6B7280',
                  fontSize: '0.875rem',
                }}
              >
                {t('auth.signInToAccount')}
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: '6px',
                }}
                onClose={() => setError('')}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <StyledTextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('auth.emailAddress')}
                name="email"
                autoComplete="email"
                autoFocus
                value={credentials.email}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />

              <Box sx={{ mb: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography
                    component="label"
                    htmlFor="password"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#111827',
                    }}
                  >
                    {t('auth.password')}
                  </Typography>
                  <Button
                    onClick={() => setForgotPasswordOpen(true)}
                    sx={{
                      color: '#0067BC',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textTransform: 'none',
                      minWidth: 'auto',
                      padding: 0,
                      '&:hover': {
                        textDecoration: 'underline',
                        background: 'transparent',
                      },
                    }}
                  >
                    {t('auth.forgotPassword')}
                  </Button>
                </Box>
                <StyledTextField
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <PrimaryButton
                type="submit"
                fullWidth
                sx={{ 
                  mt: 2, 
                  mb: 2, 
                  py: 1.5,
                }}
                disabled={loading}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: 'white' }} />
                    <span>{t('auth.signingIn')}</span>
                  </Box>
                ) : (
                  t('auth.signIn')
                )}
              </PrimaryButton>

              {/* Google Sign-In Button */}
              <Box sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Google />}
                  onClick={handleGoogleSignIn}
                  sx={{
                    borderRadius: '8px',
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderColor: '#E5E7EB',
                    color: '#6B7280',
                    '&:hover': {
                      borderColor: '#E5E7EB',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  {t('auth.signInWithGoogle')}
                </Button>
              </Box>

              <Box textAlign="center" sx={{ mt: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#6B7280',
                    fontSize: '0.875rem',
                  }}
                >
                  {t('auth.dontHaveAccount')}{' '}
                  <Link
                    to="/register"
                    style={{
                      color: '#0067BC',
                      textDecoration: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {t('auth.signUp')}
                  </Link>
                </Typography>
              </Box>
            </form>
          </AuthCard>
        </Box>
        <ForgotPasswordDialog
          open={forgotPasswordOpen}
          onClose={() => setForgotPasswordOpen(false)}
        />

        {/* MFA verification dialog */}
        <Dialog
          open={mfaDialogOpen}
          onClose={() => { setMfaDialogOpen(false); setMfaCode(''); setMfaError(''); }}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '420px' },
              maxWidth: '440px !important',
              maxHeight: '85vh',
              borderRadius: '14px',
              bgcolor: mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
              backgroundImage: 'none',
              color: mode === 'dark' ? '#F1F5F9' : '#0F172A',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'divider' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Security color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>Two-Factor Authentication</Typography>
            </Box>
            <IconButton size="small" onClick={() => { setMfaDialogOpen(false); setMfaCode(''); setMfaError(''); }} sx={{ color: mode === 'dark' ? '#94A3B8' : '#64748B' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2.5, py: 2, overflowY: 'auto' }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter the 6-digit code from your authenticator app to complete sign in.
            </Typography>
            {mfaError && <Alert severity="error" sx={{ mb: 2 }}>{mfaError}</Alert>}
            <TextField
              size="small"
              label="6-digit code"
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              fullWidth
              autoFocus
              onKeyDown={e => e.key === 'Enter' && mfaCode.length === 6 && handleMfaVerify()}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: 1, borderColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => { setMfaDialogOpen(false); setMfaCode(''); setMfaError(''); }} sx={{ color: mode === 'dark' ? '#94A3B8' : '#64748B' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleMfaVerify}
              disabled={mfaVerifying || mfaCode.length !== 6}
              sx={{ background: 'linear-gradient(45deg, #3E6AE1, #34C3FF)' }}
            >
              {mfaVerifying ? <CircularProgress size={20} /> : 'Verify'}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default Login;